import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { BackButton } from "@/components/back-button";
import { HomeNav } from "@/components/home-nav";
import { SiteFooter } from "@/components/site-footer";
import fundoDoMarAsset from "@/assets/photo-fundo-do-mar.jpg.asset.json";
import gulaAsset from "@/assets/photo-gula.jpg.asset.json";
import meninaAsset from "@/assets/photo-menina-mascara.webp.asset.json";

const PHOTO_SITE = "https://www.luizaprado.com";
/** Set to the PDF URL once the photography portfolio file is supplied. */
const PORTFOLIO_PDF: string | null = null;

const images = [
  { src: meninaAsset.url, alt: "Girl with a gas mask holding flowers — photography by Luiza Prado" },
  { src: gulaAsset.url, alt: "Gula — photography by Hifa Cybe (Luiza Prado)" },
  { src: fundoDoMarAsset.url, alt: "Fundo do Mar — photography by Luiza Prado" },
];

const SLIDE_MS = 5000;

export const Route = createFileRoute("/photo")({
  head: () => ({
    meta: [
      { title: "Photography | Luiza Jesus do Prado" },
      {
        name: "description",
        content:
          "Photography by Luiza Jesus do Prado (Luiza Prado) — selected images and the full photography portfolio at luizaprado.com.",
      },
      { property: "og:title", content: "Photography | Luiza Jesus do Prado" },
      {
        property: "og:description",
        content: "Selected photographs and the full photography portfolio at luizaprado.com.",
      },
      { property: "og:url", content: "https://www.luizajesusdoprado.com/photo" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Photo,
});

const btn =
  "inline-flex items-center justify-center gap-3 border border-bone/40 px-6 py-4 font-mono text-[10px] uppercase tracking-[0.24em] text-bone transition-colors hover:bg-bone hover:text-ink";

function Photo() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t = window.setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, SLIDE_MS);
    return () => window.clearInterval(t);
  }, [paused]);

  const current = images[index] ?? images[0]!;

  return (
    <div className="min-h-screen bg-ink text-bone">
      <HomeNav />
      <BackButton />

      {/* Full-screen slideshow */}
      <section
        className="relative h-[100svh] w-full overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {images.map((img, i) => (
          <img
            key={img.src}
            src={img.src}
            alt={img.alt}
            data-no-lightbox
            aria-hidden={i !== index}
            className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-[1200ms] ease-out ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}

        {/* Soft gradient for legibility */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-ink/80 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-ink/85 to-transparent" />

        {/* Title overlay */}
        <div className="pointer-events-none absolute left-6 top-28 md:left-16 md:top-32">
          <div className="font-mono text-[10px] uppercase leading-[1.7] tracking-[0.18em] text-bone/70">
            Photography
            <br />/ Luiza Prado
          </div>
          <h1 className="mt-6 font-display text-[clamp(2.6rem,9vw,7rem)] font-light uppercase leading-[0.85] tracking-[0.02em]">
            Photo
          </h1>
        </div>

        {/* Bottom chrome: caption, counter, controls */}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-6 px-6 pb-8 md:px-16 md:pb-10">
          <p className="max-w-md font-mono text-[10px] uppercase leading-[1.7] tracking-[0.14em] text-bone/70">
            {current.alt}
          </p>

          <div className="flex items-center gap-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-bone/70">
              {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                aria-label="Previous photograph"
                onClick={() => setIndex((i) => (i - 1 + images.length) % images.length)}
                className="flex h-11 w-11 items-center justify-center border border-bone/40 font-mono text-sm text-bone transition-colors hover:bg-bone hover:text-ink"
              >
                ←
              </button>
              <button
                type="button"
                aria-label="Next photograph"
                onClick={() => setIndex((i) => (i + 1) % images.length)}
                className="flex h-11 w-11 items-center justify-center border border-bone/40 font-mono text-sm text-bone transition-colors hover:bg-bone hover:text-ink"
              >
                →
              </button>
            </div>
          </div>
        </div>

        {/* Progress dots */}
        <div className="absolute bottom-8 left-1/2 flex -translate-x-1/2 items-center gap-2 md:bottom-10">
          {images.map((img, i) => (
            <button
              key={img.src}
              type="button"
              aria-label={`Go to photograph ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-1 transition-all duration-500 ${
                i === index ? "w-10 bg-bone" : "w-4 bg-bone/30 hover:bg-bone/60"
              }`}
            />
          ))}
        </div>
      </section>

      {/* Explanation + actions */}
      <section className="px-6 py-24 md:px-16 md:py-32">
        <span className="block h-px w-40 bg-bone/40" />
        <div className="mt-12 max-w-2xl">
          <p className="font-sans text-[15px] font-light normal-case leading-[1.8] text-bone/80 md:text-[17px]">
            My photography work now lives on its own dedicated site. This page gathers a few
            images; the complete photography portfolio — series, commissions and editorial
            work — is available at{" "}
            <a
              href={PHOTO_SITE}
              target="_blank"
              rel="noreferrer"
              className="underline decoration-bone/40 underline-offset-4 hover:decoration-bone"
            >
              luizaprado.com
            </a>
            .
          </p>

          <div className="mt-12 flex flex-col gap-4 sm:flex-row">
            {PORTFOLIO_PDF ? (
              <a href={PORTFOLIO_PDF} download className={btn}>
                Download photography portfolio ↓
              </a>
            ) : (
              <a href="/contact" className={btn}>
                Request photography portfolio (PDF)
              </a>
            )}
            <a href={PHOTO_SITE} target="_blank" rel="noreferrer" className={btn}>
              Visit luizaprado.com ↗
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
